import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Badge, Button, EmptyState, Field, Input, Select } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import {
  useCreateOptionMutation,
  useCreateOptionValueMutation,
  useDeleteOptionMutation,
  useDeleteOptionValueMutation,
  useOptionsQuery,
  useRenameOptionMutation,
  useUpdateOptionValueMutation,
} from '../api';
import type {
  ProductOptionDisplayType,
  ProductOptionValue,
  ProductOptionWithValues,
} from '../types';

import { useToast } from '@/components/ui/toast/ToastProvider';

/**
 * The colour of a « pastille ». A native colour input always holds SOME colour, so an unpicked value shows a neutral
 * grey without that grey ever being sent: only `onChange` (the merchant actually choosing) records a colour.
 */
function SwatchPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (hex: string) => void;
}): ReactElement {
  return (
    <input
      type="color"
      aria-label={label}
      title={label}
      className="h-7 w-7 cursor-pointer rounded border border-line bg-transparent p-0.5"
      value={value ?? '#cccccc'}
      onChange={(event) => {
        onChange(event.currentTarget.value);
      }}
    />
  );
}

const DISPLAY_TYPE_OPTIONS: readonly { value: ProductOptionDisplayType; labelKey: MessageKey }[] = [
  { value: 'button', labelKey: 'merchant.products.options.displayType.button' },
  { value: 'color_swatch', labelKey: 'merchant.products.options.displayType.colorSwatch' },
];

/**
 * Variant-improvements lot — the ordered option/value editor
 * (`VariantForm.tsx`'s free-form key/value bag stays as-is for the
 * ORIGINAL manual-variant path; this is the NEW structured write-side
 * source of truth `ProductOptionRepository` reads). Each option/value
 * is its own resource with its own endpoint — one mutation per
 * add/rename/delete, never one big form submit, unlike `VariantForm`.
 */
export function OptionBuilder({ productId }: { productId: string }): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const optionsQuery = useOptionsQuery(productId);
  const createOption = useCreateOptionMutation(productId);
  const renameOption = useRenameOptionMutation(productId);
  const deleteOption = useDeleteOptionMutation(productId);
  const createValue = useCreateOptionValueMutation(productId);
  const updateValue = useUpdateOptionValueMutation(productId);
  const deleteValue = useDeleteOptionValueMutation(productId);

  const [newName, setNewName] = useState('');
  const [newDisplayType, setNewDisplayType] = useState<ProductOptionDisplayType>('button');
  const [newValueByOption, setNewValueByOption] = useState<Record<string, string>>({});
  const [editingValue, setEditingValue] = useState<{ optionId: string; valueId: string } | null>(
    null,
  );
  const [editingValueText, setEditingValueText] = useState('');
  // « Pastilles de couleur » only. `undefined` = never picked, so nothing is sent and no colour is invented.
  const [editingSwatchHex, setEditingSwatchHex] = useState<string | undefined>(undefined);
  const [newSwatchByOption, setNewSwatchByOption] = useState<Record<string, string>>({});
  const [renamingOptionId, setRenamingOptionId] = useState<string | null>(null);
  const [renamingText, setRenamingText] = useState('');

  async function onCreateOption(): Promise<void> {
    if (newName.trim() === '') return;
    try {
      const option = await createOption.mutateAsync({
        name: newName.trim(),
        displayType: newDisplayType,
      });
      toast.success(t('merchant.products.options.createdToast', { name: option.name }));
      setNewName('');
      setNewDisplayType('button');
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  async function onDeleteOption(optionId: string): Promise<void> {
    if (!window.confirm(t('merchant.products.options.deleteConfirm'))) return;
    try {
      await deleteOption.mutateAsync(optionId);
      toast.success(t('merchant.products.options.deletedToast'));
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  async function onRenameOption(option: ProductOptionWithValues): Promise<void> {
    if (renamingText.trim() === '' || renamingText.trim() === option.name) {
      setRenamingOptionId(null);
      return;
    }
    try {
      await renameOption.mutateAsync({
        optionId: option.id,
        values: { name: renamingText.trim(), displayType: option.displayType },
      });
      setRenamingOptionId(null);
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  async function onAddValue(optionId: string): Promise<void> {
    const value = (newValueByOption[optionId] ?? '').trim();
    if (value === '') return;
    const swatchHex = newSwatchByOption[optionId];
    try {
      const created = await createValue.mutateAsync({
        optionId,
        values: swatchHex === undefined ? { value } : { value, swatchHex },
      });
      toast.success(t('merchant.products.options.valueCreatedToast', { value: created.value }));
      setNewValueByOption((previous) => ({ ...previous, [optionId]: '' }));
      setNewSwatchByOption((previous) => {
        const { [optionId]: _picked, ...rest } = previous;
        return rest;
      });
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  async function onSaveValue(optionId: string, valueId: string): Promise<void> {
    if (editingValueText.trim() === '') return;
    try {
      const value = editingValueText.trim();
      await updateValue.mutateAsync({
        optionId,
        valueId,
        values: editingSwatchHex === undefined ? { value } : { value, swatchHex: editingSwatchHex },
      });
      setEditingValue(null);
      setEditingSwatchHex(undefined);
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  async function onDeleteValue(optionId: string, valueId: string): Promise<void> {
    if (!window.confirm(t('merchant.products.options.deleteValueConfirm'))) return;
    try {
      await deleteValue.mutateAsync({ optionId, valueId });
      toast.success(t('merchant.products.options.valueDeletedToast'));
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="font-display text-lg font-bold text-ink">
        {t('merchant.products.options.title')}
      </h2>

      {optionsQuery.isPending ? (
        <p className="text-sm text-inkFaint">{t('merchant.common.loadingAria')}…</p>
      ) : optionsQuery.isError ? (
        <EmptyState
          title={t('merchant.products.options.loadError')}
          description={t(resolveErrorMessageKey(optionsQuery.error))}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {optionsQuery.data.length === 0 ? (
            <p className="text-sm text-inkFaint">{t('merchant.products.options.empty')}</p>
          ) : null}

          {optionsQuery.data.map((option) => (
            <div key={option.id} className="flex flex-col gap-2 rounded-md border border-line p-3">
              <div className="flex items-center justify-between gap-2">
                {renamingOptionId === option.id ? (
                  <div className="flex flex-1 items-center gap-2">
                    <Input
                      autoFocus
                      value={renamingText}
                      onChange={(event) => {
                        setRenamingText(event.currentTarget.value);
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        void onRenameOption(option);
                      }}
                    >
                      {t('merchant.common.save')}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink">{option.name}</span>
                    {option.autoGenerated ? (
                      <Badge tone="info">{t('merchant.products.options.autoGeneratedBadge')}</Badge>
                    ) : null}
                  </div>
                )}

                {renamingOptionId === option.id ? null : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setRenamingOptionId(option.id);
                        setRenamingText(option.name);
                      }}
                    >
                      {t('merchant.products.options.rename')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        void onDeleteOption(option.id);
                      }}
                    >
                      {t('merchant.products.options.delete')}
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {option.values.map((value: ProductOptionValue) =>
                  editingValue?.optionId === option.id && editingValue.valueId === value.id ? (
                    <div key={value.id} className="flex items-center gap-1">
                      {option.displayType === 'color_swatch' ? (
                        <SwatchPicker
                          label={t('merchant.products.options.swatchColor')}
                          value={editingSwatchHex ?? value.swatchHex}
                          onChange={setEditingSwatchHex}
                        />
                      ) : null}
                      <Input
                        autoFocus
                        className="w-28"
                        value={editingValueText}
                        onChange={(event) => {
                          setEditingValueText(event.currentTarget.value);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void onSaveValue(option.id, value.id);
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          void onSaveValue(option.id, value.id);
                        }}
                      >
                        {t('merchant.common.save')}
                      </Button>
                    </div>
                  ) : (
                    <span
                      key={value.id}
                      className="flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-xs text-inkSoft"
                    >
                      {option.displayType === 'color_swatch' && value.swatchHex !== null ? (
                        <span
                          aria-hidden="true"
                          className="h-3 w-3 rounded-full border border-line"
                          style={{ backgroundColor: value.swatchHex }}
                        />
                      ) : null}
                      {value.value}
                      <button
                        type="button"
                        className="text-inkFaint hover:text-ink"
                        onClick={() => {
                          setEditingValue({ optionId: option.id, valueId: value.id });
                          setEditingValueText(value.value);
                          setEditingSwatchHex(undefined);
                        }}
                      >
                        {t('merchant.products.options.editValue')}
                      </button>
                      <button
                        type="button"
                        className="text-inkFaint hover:text-critical"
                        onClick={() => {
                          void onDeleteValue(option.id, value.id);
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ),
                )}

                <div className="flex items-center gap-1">
                  {option.displayType === 'color_swatch' ? (
                    <SwatchPicker
                      label={t('merchant.products.options.swatchColor')}
                      value={newSwatchByOption[option.id] ?? null}
                      onChange={(hex) => {
                        setNewSwatchByOption((previous) => ({ ...previous, [option.id]: hex }));
                      }}
                    />
                  ) : null}
                  <Input
                    placeholder={t('merchant.products.options.valuePlaceholder')}
                    className="w-28"
                    value={newValueByOption[option.id] ?? ''}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setNewValueByOption((previous) => ({ ...previous, [option.id]: value }));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void onAddValue(option.id);
                      }
                    }}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      void onAddValue(option.id);
                    }}
                  >
                    {t('merchant.products.options.addValue')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onCreateOption();
        }}
        className="flex items-end gap-2"
      >
        <Field label={t('merchant.products.options.title')}>
          {(bind) => (
            <Input
              {...bind}
              placeholder={t('merchant.products.options.namePlaceholder')}
              value={newName}
              onChange={(event) => {
                setNewName(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        <Field label={t('merchant.products.options.displayTypeLabel')}>
          {(bind) => (
            <Select
              {...bind}
              options={DISPLAY_TYPE_OPTIONS.map((option) => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
              required
              value={newDisplayType}
              onChange={(event) => {
                setNewDisplayType(event.currentTarget.value as ProductOptionDisplayType);
              }}
            />
          )}
        </Field>
        <Button type="submit" loading={createOption.isPending}>
          {t('merchant.products.options.add')}
        </Button>
      </form>
    </div>
  );
}
